import React, { useState, useEffect } from 'react';
import { commandTemplatesApi } from '../../utils/api';
// Indicateur d'activation de l'intégration Shopify (REACT_APP_ENABLE_SHOPIFY)
import { SHOPIFY_ENABLED } from '../../config/features';
import {
  Table,
  Button,
  Card,
  Input,
  Typography,
  Switch,
  Modal,
  Form,
  Space,
  message,
  Tag,
  Popconfirm,
  List,
  Tooltip,
  Badge,
  Grid,
} from 'antd';
import ColumnsCardList from '../common/ColumnsCardList';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { TextArea } = Input;

const CommandTemplates = () => {
  // Mobile (< md) : cartes à la place du tableau ; `=== false` évite un flash avant la mesure
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [templates, setTemplates] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [paramModalVisible, setParamModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [paramForm] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);

  // Charger les templates via l'API
  const loadTemplates = async () => {
    try {
      const response = await commandTemplatesApi.getAll();
      if (response.data?.success) {
        setTemplates(response.data.data);
      } else {
        message.error('Erreur lors du chargement des templates');
      }
    } catch (error) {
      console.error('Erreur chargement templates:', error);
      message.error('Erreur lors du chargement des templates');
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const handleOpenModal = (template = null) => {
    setEditingTemplate(template);
    if (template) {
      form.setFieldsValue({
        ...template,
        parameters: template.parameters || [],
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        parameters: [],
        active: true,
      });
    }
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingTemplate(null);
    form.resetFields();
  };

  const handleAddParameter = () => {
    const parameters = form.getFieldValue('parameters') || [];
    paramForm.resetFields();
    setParamModalVisible(true);
  };

  const handleParamSubmit = (values) => {
    const parameters = form.getFieldValue('parameters') || [];
    form.setFieldsValue({
      parameters: [...parameters, values],
    });
    setParamModalVisible(false);
    paramForm.resetFields();
  };

  const handleRemoveParameter = (index) => {
    const parameters = form.getFieldValue('parameters');
    form.setFieldsValue({
      parameters: parameters.filter((_, i) => i !== index),
    });
  };


  // Supprimer un template via l'API
  const handleDelete = async (template) => {
    try {
      const response = await commandTemplatesApi.delete(template.id);
      if (response.data?.success) {
        message.success('Template supprimé avec succès');
        loadTemplates();
      } else {
        message.error(response.data?.message || 'Erreur lors de la suppression du template');
      }
    } catch (error) {
      console.error('Erreur suppression template:', error);
      message.error(error.response?.data?.message || 'Erreur lors de la suppression du template');
    }
  };

  const getParameterHelp = (parameters) => {
    if (!parameters?.length) return '';
    return parameters.map(p => `{${p.name}}`).join(', ');
  };

  // Liste des codes de templates protégés (non supprimables/modifiables pour paramètres)
  const protectedTemplates = ['MSGLIVREUR', 'MSG2JOURS', 'MSG7JOURS'];

  // Vérifie si un template est protégé
  const isDefaultTemplate = (code) => {
    return protectedTemplates.includes(code);
  };

  // Colonnes dédiées aux cartes mobile : contenu complet (pas de troncature) et boutons avec libellés
  const cardColumns = [
    { title: 'Nom', dataIndex: 'name', key: 'name', render: (text, record) => (
      <div className="flex flex-wrap items-center gap-2">
        <Tag color="blue" className="private-tag-accent" >{record.code}</Tag>
        <span className="font-medium">{text}</span>
      </div>
    ) },
    { title: 'Message', dataIndex: 'message_template', key: 'message_template', render: (text) => (
      <span className="block whitespace-pre-wrap break-words text-left text-sm text-gray-700">{text}</span>
    ) },
    { title: 'Paramètres', dataIndex: 'parameters', key: 'parameters', render: (parameters) => (
      parameters?.length > 0 ? (
        <Space size={[4, 4]} wrap>
          {parameters.map((param, index) => (
            <Tag key={index} color={param.required ? 'red' : 'green'}>{param.name}</Tag>
          ))}
        </Space>
      ) : '—'
    ) },
    { title: 'Statut', dataIndex: 'active', key: 'active', render: (active) => (
      <Tag icon={active ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={active ? 'success' : 'error'}>
        {active ? 'Actif' : 'Inactif'}
      </Tag>
    ) },
    { title: 'Actions', key: 'actions', render: (_, record) => (
      <Space>
        <Button type="primary" icon={<EditOutlined />} onClick={() => handleOpenModal(record)}>Modifier</Button>
        <Popconfirm
          title="Êtes-vous sûr de vouloir supprimer ce template ?"
          onConfirm={() => handleDelete(record)}
          okText="Oui"
          cancelText="Non"
          placement="top"
        >
          <Button type="primary" danger icon={<DeleteOutlined />} disabled={isDefaultTemplate(record.code)}>Supprimer</Button>
        </Popconfirm>
      </Space>
    ) },
  ];

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: window.innerWidth < 768 ? 80 : 120,
      render: (text) => (
        <Tag color="blue" size={window.innerWidth < 768 ? 'small' : 'default'} className="private-tag-accent" >
          {window.innerWidth < 768 ? text.substring(0, 6) : text}
        </Tag>
      ),
    },
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      width: window.innerWidth < 768 ? 100 : 200,
      render: (text) => (
        <span className={`${window.innerWidth < 768 ? 'text-xs truncate block' : ''}`}>
          {text}
        </span>
      ),
    },
    {
      title: 'Message',
      dataIndex: 'message_template',
      key: 'message_template',
      width: window.innerWidth < 768 ? 120 : 300,
      render: (text, record) => (
        <div className="w-full">
          <div className={`${
            window.innerWidth < 768 
              ? 'max-w-[100px]' 
              : 'max-w-[280px]'
          } whitespace-nowrap overflow-hidden text-ellipsis`}>
            <Tooltip 
              title={text.split('\n').map((line, i) => (
                <div key={i}>{line}</div>
              ))}
              placement={window.innerWidth < 768 ? 'top' : 'topLeft'}
            >
              <span className={window.innerWidth < 768 ? 'text-xs' : ''}>
                {text}
              </span>
            </Tooltip>
          </div>
          {record.parameters?.length > 0 && window.innerWidth >= 768 && (
            <div className="mt-1">
              <Space size={[0, 4]} wrap>
                {record.parameters.slice(0, 3).map((param, index) => (
                  <Tag key={index} color={param.required ? 'red' : 'green'} size="small">
                    {param.name}
                  </Tag>
                ))}
                {record.parameters.length > 3 && (
                  <Tag size="small">+{record.parameters.length - 3}</Tag>
                )}
              </Space>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Paramètres',
      dataIndex: 'parameters',
      key: 'parameters',
      responsive: window.innerWidth < 768 ? ['md'] : undefined,
      render: (parameters) => (
        <Space wrap>
          {(parameters || []).slice(0, 2).map((param, index) => (
            <Tag key={index} color="blue" size="small" className="private-tag-accent" >
              {param.name}
            </Tag>
          ))}
          {parameters?.length > 2 && (
            <Tag size="small">+{parameters.length - 2}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'active',
      key: 'active',
      width: window.innerWidth < 768 ? 60 : undefined,
      render: (active) => (
        <Tag 
          icon={active ? <CheckCircleOutlined /> : <CloseCircleOutlined />} 
          color={active ? 'success' : 'error'}
          size={window.innerWidth < 768 ? 'small' : 'default'}
        >
          {window.innerWidth < 768 ? (active ? 'On' : 'Off') : (active ? 'Actif' : 'Inactif')}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: window.innerWidth < 768 ? 80 : undefined,
      render: (_, record) => (
        <div className={window.innerWidth < 768 ? 'flex flex-col gap-1' : 'flex gap-2'}>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
            size={window.innerWidth < 768 ? 'small' : 'middle'}
            className={window.innerWidth < 768 ? 'min-h-[28px]' : ''}
          >
            {window.innerWidth >= 768 ? 'Modifier' : ''}
          </Button>
          <Popconfirm
            title="Êtes-vous sûr de vouloir supprimer ce template ?"
            onConfirm={() => handleDelete(record)}
            okText="Oui"
            cancelText="Non"
            placement={window.innerWidth < 768 ? 'top' : 'left'}
          >
            <Button 
              type="primary" 
              danger 
              icon={<DeleteOutlined />}
              size={window.innerWidth < 768 ? 'small' : 'middle'}
              className={window.innerWidth < 768 ? 'min-h-[28px]' : ''}
              disabled={record.code === 'MSGLIVREUR' || record.code === 'MSG2JOURS' || record.code === 'MSG7JOURS'}
            >
              {window.innerWidth >= 768 ? 'Supprimer' : ''}
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  // Créer les templates par défaut via l'API
  const createDefaultTemplates = async () => {
    const confirmed = window.confirm(
      "Voulez-vous créer les templates par défaut  ?"
    );
    if (confirmed) {
      try {
        setIsLoading(true);
        const response = await commandTemplatesApi.createDefaults();
        if (response.data?.success) {
          loadTemplates();
          message.success(response.data.message || 'Templates créés avec succès !');
        } else {
          message.error('Erreur lors de la création des templates');
        }
      } catch (error) {
        console.error('Erreur lors de la création des templates:', error);
        message.error('Erreur lors de la création des templates');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Sauvegarder un template via l'API (création ou modification)
  const handleSave = async (values) => {
    try {
      if (editingTemplate) {
        // Modification via l'API
        const updateData = {
          ...values,
          parameters: values.parameters || editingTemplate.parameters || []
        };

        const response = await commandTemplatesApi.update(editingTemplate.id, updateData);
        if (response.data?.success) {
          message.success('Template modifié avec succès');
        } else {
          throw new Error(response.data?.message || 'Erreur lors de la modification');
        }
      } else {
        // Création via l'API
        const createData = {
          ...values,
          parameters: values.parameters || []
        };

        const response = await commandTemplatesApi.create(createData);
        if (response.data?.success) {
          message.success('Template créé avec succès');
        } else {
          throw new Error(response.data?.message || 'Erreur lors de la création');
        }
      }

      setModalVisible(false);
      form.resetFields();
      setEditingTemplate(null);
      loadTemplates();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      message.error(error.response?.data?.message || error.message || 'Erreur lors de la sauvegarde');
    }
  };

  return (
    <div className={`${window.innerWidth < 768 ? 'p-4' : 'p-6'}`}>
      <Card className={window.innerWidth < 768 ? 'p-2' : ''}>
        <div className={`${
          window.innerWidth < 768 
            ? 'flex flex-col gap-4 mb-4' 
            : 'flex justify-between items-center mb-6'
        }`}>
          <Title level={window.innerWidth < 768 ? 5 : 4} className={window.innerWidth < 768 ? 'text-sm mb-0' : ''}>
            <span className="hidden sm:inline">Templates de Commandes WhatsApp</span>
            <span className="sm:hidden">Templates</span>
          </Title>
          <div className={`${
            window.innerWidth < 768 
              ? 'flex flex-col gap-2 w-full' 
              : 'space-x-2'
          }`}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
              size={window.innerWidth < 768 ? 'small' : 'middle'}
              className={window.innerWidth < 768 ? 'w-full min-h-[40px]' : ''}
            >
              {window.innerWidth < 768 ? 'Nouveau' : 'Nouveau Template'}
            </Button>
            {/* Modèles Shopify par défaut : masqués quand l'intégration est désactivée */}
            {SHOPIFY_ENABLED && (
              <Button
                onClick={createDefaultTemplates}
                className={`bg-green-500 text-white rounded hover:bg-green-600 ${
                  window.innerWidth < 768
                    ? 'w-full min-h-[40px] text-sm px-3 py-2'
                    : 'px-4 py-2'
                }`}
                size={window.innerWidth < 768 ? 'small' : 'middle'}
              >
                {window.innerWidth < 768 ? 'Templates Shopify' : 'Créer templates Shopify'}
              </Button>
            )}
          </div>
        </div>

        {isMobile ? (
          /* Mobile : une carte par template (code + nom en haut, message complet, paramètres, statut, actions) */
          <ColumnsCardList
            columns={cardColumns}
            dataSource={templates}
            headerKeys={['name']}
            actionKeys={['actions']}
            clientPageSize={5}
            emptyText="Aucun template"
          />
        ) : (
        <Table
          columns={columns}
          dataSource={templates}
          rowKey="id"
          pagination={{ 
            pageSize: window.innerWidth < 768 ? 5 : 10,
            showSizeChanger: window.innerWidth > 768,
            showQuickJumper: window.innerWidth > 768,
            showTotal: (total, range) => 
              window.innerWidth > 768 
                ? `${range[0]}-${range[1]} sur ${total} templates`
                : `${total} templates`
          }}
          scroll={{ 
            x: window.innerWidth < 768 ? 600 : 1200,
            scrollToFirstRowOnChange: true
          }}
          size={window.innerWidth < 768 ? 'small' : 'middle'}
          className={window.innerWidth < 768 ? 'overflow-x-auto' : ''}
        />
        )}

        <Modal
          title={<span className={window.innerWidth < 768 ? 'text-sm' : ''}>
            {editingTemplate ? 'Modifier le Template' : 'Nouveau Template'}
          </span>}
          open={modalVisible}
          onCancel={handleCloseModal}
          footer={null}
          width={window.innerWidth < 768 ? '95vw' : 800}
          centered={window.innerWidth < 768}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            initialValues={{
              active: true,
              parameters: [],
            }}
          >
            <Form.Item
              name="code"
              label={<span className={window.innerWidth < 768 ? 'text-sm' : ''}>
                Code
              </span>}
              rules={[{ required: true, message: 'Le code est requis' }]}
            >
              <Input 
                placeholder={window.innerWidth < 768 ? 'ex: RELANCE' : 'ex: RELANCE'} 
                size={window.innerWidth < 768 ? 'small' : 'middle'}
              />
            </Form.Item>

            <Form.Item
              name="name"
              label={<span className={window.innerWidth < 768 ? 'text-sm' : ''}>
                Nom
              </span>}
              rules={[{ required: true, message: 'Le nom est requis' }]}
            >
              <Input 
                placeholder={window.innerWidth < 768 ? 'ex: Relance client' : 'ex: Relance client'} 
                size={window.innerWidth < 768 ? 'small' : 'middle'}
              />
            </Form.Item>

            <Form.Item
              label={<span className={window.innerWidth < 768 ? 'text-sm' : ''}>
                Paramètres
              </span>}
              required={false}
            >
              <Form.List name="parameters">
                {(fields, { add, remove }) => (
                  <>
                    <List
                      size="small"
                      bordered
                      dataSource={fields}
                      renderItem={(field, index) => (
                        <List.Item
                          style={{ flexWrap: 'wrap', rowGap: 8 }}
                          actions={[
                            !isDefaultTemplate(form.getFieldValue('code')) && (
                              <Button
                                key="delete"
                                type="link"
                                danger
                                onClick={() => handleRemoveParameter(index)}
                                size={window.innerWidth < 768 ? 'small' : 'middle'}
                                className={window.innerWidth < 768 ? 'text-xs' : ''}
                              >
                                {window.innerWidth < 768 ? 'Supp.' : 'Supprimer'}
                              </Button>
                            ),
                          ].filter(Boolean)}
                        >
                          {/* Mobile : 3 champs empilés en pleine largeur ; dès sm : sur une ligne */}
                          <div className="grid w-full grid-cols-1 gap-2 sm:flex sm:w-auto sm:items-center">
                            <Form.Item
                              name={[field.name, 'name']}
                              noStyle
                            >
                              <Input disabled className="w-full sm:w-[120px]" />
                            </Form.Item>
                            <Form.Item
                              name={[field.name, 'label']}
                              noStyle
                            >
                              <Input disabled className="w-full sm:w-[120px]" />
                            </Form.Item>
                            <Form.Item
                              name={[field.name, 'required']}
                              valuePropName="checked"
                              noStyle
                            >
                              <Switch
                                disabled
                                checkedChildren="Requis"
                                unCheckedChildren="Optionnel"
                              />
                            </Form.Item>
                          </div>
                        </List.Item>
                      )}
                    />
                    {!isDefaultTemplate(form.getFieldValue('code')) && (
                      <Button
                        type="dashed"
                        onClick={() => handleAddParameter()}
                        style={{ width: '100%', marginTop: '10px' }}
                        icon={<PlusOutlined />}
                        size={window.innerWidth < 768 ? 'small' : 'middle'}
                        className={window.innerWidth < 768 ? 'min-h-[40px] text-sm' : ''}
                      >
                        {window.innerWidth < 768 ? 'Ajouter param.' : 'Ajouter un paramètre'}
                      </Button>
                    )}
                  </>
                )}
              </Form.List>
            </Form.Item>

            <Form.Item
              name="message_template"
              label={<span className={window.innerWidth < 768 ? 'text-sm' : ''}>
                Message Template
              </span>}
              rules={[{ required: true, message: 'Le message est requis' }]}
              help={
                <Text type="secondary" className={window.innerWidth < 768 ? 'text-xs' : ''}>
                  {window.innerWidth < 768 ? 'Params: ' : 'Paramètres disponibles: '}
                  {getParameterHelp(form.getFieldValue('parameters'))}
                </Text>
              }
            >
              <TextArea
                rows={window.innerWidth < 768 ? 3 : 4}
                placeholder={window.innerWidth < 768 
                  ? 'ex: Bonjour! Nous avons remarqué...'
                  : 'ex: Bonjour! Nous avons remarqué votre intérêt pour {article}...'
                }
                size={window.innerWidth < 768 ? 'small' : 'middle'}
              />
            </Form.Item>

            <Form.Item 
              name="active" 
              valuePropName="checked" 
              label={<span className={window.innerWidth < 768 ? 'text-sm' : ''}>
                Actif
              </span>}
            >
              <Switch 
                size={window.innerWidth < 768 ? 'small' : 'default'}
                checkedChildren={window.innerWidth < 768 ? 'On' : 'Actif'} 
                unCheckedChildren={window.innerWidth < 768 ? 'Off' : 'Inactif'}
              />
            </Form.Item>

            <Form.Item className={window.innerWidth < 768 ? 'mt-4' : 'text-right'}>
              <div className={`${
                window.innerWidth < 768 
                  ? 'flex flex-col gap-2 w-full' 
                  : 'text-right'
              }`}>
                <Space className={window.innerWidth < 768 ? 'w-full' : ''}>
                  <Button 
                    onClick={handleCloseModal}
                    size={window.innerWidth < 768 ? 'small' : 'middle'}
                    className={window.innerWidth < 768 ? 'min-h-[40px] flex-1' : ''}
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    size={window.innerWidth < 768 ? 'small' : 'middle'}
                    className={window.innerWidth < 768 ? 'min-h-[40px] flex-1' : ''}
                  >
                    {editingTemplate ? 'Modifier' : 'Créer'}
                  </Button>
                </Space>
              </div>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title={<span className={window.innerWidth < 768 ? 'text-sm' : ''}>
            {window.innerWidth < 768 ? 'Paramètre' : 'Ajouter un paramètre'}
          </span>}
          open={paramModalVisible}
          onCancel={() => setParamModalVisible(false)}
          footer={null}
          width={window.innerWidth < 768 ? '95vw' : 600}
          centered={window.innerWidth < 768}
        >
          <Form
            form={paramForm}
            layout="vertical"
            onFinish={handleParamSubmit}
          >
            <Form.Item
              name="name"
              label={<span className={window.innerWidth < 768 ? 'text-sm' : ''}>
                {window.innerWidth < 768 ? 'Nom param.' : 'Nom du paramètre'}
              </span>}
              rules={[
                { required: true, message: 'Le nom est requis' },
                {
                  pattern: /^[a-zA-Z][a-zA-Z0-9_]*$/,
                  message: window.innerWidth < 768 
                    ? 'Format invalide' 
                    : 'Le nom doit commencer par une lettre et ne contenir que des lettres, chiffres et _',
                },
              ]}
              tooltip={window.innerWidth < 768 
                ? 'Nom utilisé dans le template. Ex: {nom}'
                : 'Ce nom sera utilisé dans le template entre accolades. Ex: {nom}'
              }
            >
              <Input 
                placeholder="ex: prix" 
                size={window.innerWidth < 768 ? 'small' : 'middle'}
              />
            </Form.Item>

            <Form.Item
              name="label"
              label={<span className={window.innerWidth < 768 ? 'text-sm' : ''}>
                Label
              </span>}
              rules={[{ required: true, message: 'Le label est requis' }]}
              tooltip={window.innerWidth < 768 
                ? 'Label affiché'
                : 'Label affiché dans l\'interface'
              }
            >
              <Input 
                placeholder={window.innerWidth < 768 ? 'ex: Prix' : 'ex: Prix du produit'} 
                size={window.innerWidth < 768 ? 'small' : 'middle'}
              />
            </Form.Item>

            <Form.Item
              name="required"
              valuePropName="checked"
              label={<span className={window.innerWidth < 768 ? 'text-sm' : ''}>
                Requis
              </span>}
            >
              <Switch 
                size={window.innerWidth < 768 ? 'small' : 'default'}
                checkedChildren={window.innerWidth < 768 ? 'Oui' : 'Requis'} 
                unCheckedChildren={window.innerWidth < 768 ? 'Non' : 'Optionnel'}
              />
            </Form.Item>

            <Form.Item className={window.innerWidth < 768 ? 'mt-4' : 'text-right'}>
              <div className={`${
                window.innerWidth < 768 
                  ? 'flex flex-col gap-2 w-full' 
                  : 'text-right'
              }`}>
                <Space className={window.innerWidth < 768 ? 'w-full' : ''}>
                  <Button 
                    onClick={() => setParamModalVisible(false)}
                    size={window.innerWidth < 768 ? 'small' : 'middle'}
                    className={window.innerWidth < 768 ? 'min-h-[40px] flex-1' : ''}
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    size={window.innerWidth < 768 ? 'small' : 'middle'}
                    className={window.innerWidth < 768 ? 'min-h-[40px] flex-1' : ''}
                  >
                    Ajouter
                  </Button>
                </Space>
              </div>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default CommandTemplates;
